import * as React from 'react';
import {
  CloseOutlined,
  FilterOutlined,
  HistoryOutlined,
  PaperClipOutlined,
  SearchOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { Badge, Button, Flex, Input, InputRef, Modal, Tooltip } from 'antd';
import clsx from 'clsx';
import { FileWithBackendId, useUpload } from '@hooks/useUpload/useUpload';
import {
  ATTACH_FILES,
  ATTACHMENTS,
  CHAT_INPUT_PLACEHOLDER,
  DISPLAY_UPLOAD,
  UPLOAD_MORE,
} from '@constants/chat';
import { CLOSE_BUTTON_LABEL, SUBMIT_BUTTON_LABEL } from '@constants/common';
import { PipelineOutputType } from '@constants/pipelines';
import { SEARCH_BUTTON, SEARCHBAR_PLACEHOLDER } from '@constants/search';
import { Attachment, UploadTriggerRef } from '@components/Attachment/Attachment';
import styles from './ChatInput.module.scss';

const { TextArea } = Input;

type ChatProps = {
  hasActiveFilters: boolean;
  displayQueryHistory: boolean;
  onToggleHistory?: () => void;
  onToggleFiltersClick?: () => void;
  type: PipelineOutputType.CHAT;
};

type GenerativeProps = {
  type: PipelineOutputType.GENERATIVE;
};

type FallbackProps = {
  type: Exclude<PipelineOutputType, PipelineOutputType.CHAT | PipelineOutputType.GENERATIVE>;
};

type Props = {
  type: PipelineOutputType;
  activatingPipeline: boolean;
  isChatting: boolean;
  onSubmit: (_: string, ids: string[]) => Promise<void>;
  allowUpload: boolean;
  ctoText?: string;
} & (GenerativeProps | ChatProps | FallbackProps);

export const ChatInput: React.FC<Props> = ({
  activatingPipeline,
  isChatting,
  onSubmit,
  allowUpload,
  type,
  ctoText,
  ...props
}) => {
  const { files, onFileUpload, onRemoveFile, clearAllFiles } = useUpload();
  const chatPipeline = type === PipelineOutputType.CHAT;
  const generativeProps = chatPipeline ? (props as ChatProps) : null;
  const { hasActiveFilters, displayQueryHistory, onToggleFiltersClick, onToggleHistory } =
    generativeProps ?? {};

  const [preview, setPreview] = React.useState<
    (FileWithBackendId['data'] & { name: string }) | null
  >(null);

  const [chatQuery, setChatQuery] = React.useState('');
  const [uploadVisible, setUploadVisible] = React.useState(false);

  const isTextAreaDisabled = isChatting || activatingPipeline;
  const isSubmitDisabled = !chatQuery || activatingPipeline || isChatting;

  const inputRef = React.useRef<InputRef>(null);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submitQuery = async () => {
    await onSubmit(
      chatQuery,
      files.map((f) => f.id),
    );
    clearAllFiles();
    setChatQuery('');
    setUploadVisible(false);
  };

  const handleSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    submitQuery();
  };

  const handleKeyDown = (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isEnter = ev.key === 'Enter';
    const modifierPressed = ev.metaKey || ev.ctrlKey || ev.shiftKey;

    if (isEnter && !modifierPressed && !isSubmitDisabled) {
      ev.preventDefault();
      submitQuery();
    }
  };

  const handleChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
    setChatQuery(ev.target.value);
  };

  const uploadRef = React.useRef<UploadTriggerRef>(null);

  const handleClickAttach = () => {
    if (uploadVisible) {
      uploadRef.current?.trigger();
    } else {
      setUploadVisible(true);
    }
  };

  const notifyAttachments = React.useMemo(() => {
    return !uploadVisible && !!files.length;
  }, [uploadVisible, files]);

  const onPreview = async (id?: string) => {
    const result = files.find((file) => file.id == id);
    if (result && result?.data.isImage) {
      setPreview({ ...result.data, name: result.file.name });
    } else {
      setPreview(null);
    }
  };

  return (
    <div className={styles.chatInput}>
      {chatPipeline && (
        <Badge dot={hasActiveFilters} className={styles.chatMobileFilter}>
          <Button aria-label="filter" icon={<FilterOutlined />} onClick={onToggleFiltersClick} />
        </Badge>
      )}

      <Flex className={styles.chatWrapper} vertical>
        {uploadVisible && (
          <>
            <Flex className={styles.chatUpload} vertical>
              <Flex className={styles.chatUpload_header}>
                <p>{ATTACHMENTS}</p>
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={() => setUploadVisible(false)}
                />
              </Flex>
            </Flex>

            <div className={styles.chatUpload_prompt}>
              <Attachment
                ref={uploadRef}
                onChange={onFileUpload}
                onRemoveFiles={onRemoveFile}
                files={files}
                onPreview={onPreview}
              />
            </div>
          </>
        )}

        <div
          className={clsx(styles.chatContainer, {
            [styles.chatContainerBorder]: !uploadVisible,
            [styles.chatContainerBorder_top]: uploadVisible,
          })}
        >
          {allowUpload && (
            <Tooltip title={ATTACH_FILES}>
              <Badge dot={notifyAttachments}>
                <Button
                  className={styles.chatAttachButton}
                  icon={<PaperClipOutlined className={styles.chatAttachButton_icon} />}
                  type="text"
                  aria-label={uploadVisible ? UPLOAD_MORE : DISPLAY_UPLOAD}
                  data-testid="uploadFiles"
                  onClick={handleClickAttach}
                />
              </Badge>
            </Tooltip>
          )}

          {displayQueryHistory && (
            <Button
              aria-label="history"
              type="link"
              size="small"
              icon={<HistoryOutlined />}
              onClick={onToggleHistory}
            />
          )}

          <form onSubmit={handleSubmit} className={styles.chatForm}>
            <TextArea
              ref={inputRef}
              name="query"
              className={styles.chatFormInputContainer}
              placeholder={chatPipeline ? CHAT_INPUT_PLACEHOLDER : SEARCHBAR_PLACEHOLDER}
              disabled={isTextAreaDisabled}
              value={chatQuery}
              onChange={handleChange}
              autoFocus
              autoSize={{ maxRows: 4 }}
              onKeyDown={handleKeyDown}
              data-testid="query_input"
            />

            <Button
              aria-label="submit"
              type="primary"
              htmlType="submit"
              icon={chatPipeline ? <SendOutlined /> : <SearchOutlined />}
              disabled={isSubmitDisabled}
              loading={isChatting}
              className={clsx({
                [styles.chatFormButton]: !chatPipeline,
                [styles.chatFormButton_upload]: uploadVisible,
              })}
              data-testid="query_button"
            >
              {
                <span className={styles.chatFormSubmitText}>
                  {chatQuery && chatPipeline && SUBMIT_BUTTON_LABEL}
                  {!chatPipeline && (ctoText || SEARCH_BUTTON)}
                </span>
              }
            </Button>
          </form>
        </div>
      </Flex>

      <Modal
        className={clsx(styles.chatInputPreview, {
          [styles.chatInputPreviewImg]: preview?.isImage,
        })}
        centered
        open={!!preview}
        title={null}
        onCancel={() => onPreview()}
        transitionName=""
        footer={null}
      >
        {preview?.isImage && (
          <div className={styles.chatInputPreviewContainer}>
            <Button className={styles.chatInputPreviewClose} onClick={() => onPreview()}>
              <CloseOutlined />
              {CLOSE_BUTTON_LABEL}
            </Button>
            <img className={styles.chatInputPreview_img} src={preview?.url} alt={preview?.name} />
            {preview.name}
          </div>
        )}
      </Modal>
    </div>
  );
};
